import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData
} from "@whitespace/whitespace-ui";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import IAddVehicleRequestModel from "interfaces/Request/IAddVehicleRequestModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import vehicleSchema from "modules/schemas/vehicleSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IGetTrackingDevicesResponseModel from "interfaces/Response/IGetTrackingDevicesResponseModel";
import "styles/sf-base.scss";
import "styles/sf-dropdown.scss";
import IRegion from "interfaces/IRegion";
import { WarningConfirmation } from "components/WarningConfirmation/WarningConfirmation";

const AddVehicle = () => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IAddVehicleRequestModel>({
    resolver: yupResolver(vehicleSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: getUnallocatedTrackingDevicesIsSending,
    errors: getUnallocatedTrackingDevicesErrors,
    data: getUnallocatedTrackingDevicesData,
    sendRequest: getUnallocatedTrackingDevicesSendRequest
  } = useAmplifyREST();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addVehicleIsSending,
    errors: addVehicleErrors,
    statusCode: addVehicleStatusCode,
    sendRequest: addVehicleSendRequest,
    data: addVehicleResponseData
  } = useAmplifyREST();

  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const isSending = getUnallocatedTrackingDevicesIsSending
    || getRegionsIsSending
    || addVehicleIsSending
    || !aaFixDropDownTreeCompleted;

  const navigate = useNavigate();

  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [selectedRegions, setSelectedRegions] = useState<
  string[] | undefined
  >();
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [warningMessage, setWarningMessage] = useState<string | undefined>();
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false);

  const regionFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: true };

  const canFormBeSubmitted = () => {
    setAddButtonDisabled(
      !(
        getValues("name").length > 0
        && getValues("registration").length > 0
        && getValues("type").length > 0
        && selectedRegions
        && selectedRegions.length > 0
      )
    );
  };

  const watchFields = watch([
    "name",
    "registration",
    "aOmPassword",
    "type",
    "active",
    "trackingId"
  ]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedRegions]);

  const dropDownTreeComponentOnChange = () => {
    setSelectedRegions(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    getUnallocatedTrackingDevicesSendRequest("tracking/unallocated", "GET");
  }, [getUnallocatedTrackingDevicesSendRequest]);

  useEffect(() => {
    if (getUnallocatedTrackingDevicesErrors) {
      setApiErrors(current => [
        ...current,
        ...getUnallocatedTrackingDevicesErrors
      ]);
      setShowExceptions(true);
    }
  }, [getUnallocatedTrackingDevicesErrors, setApiErrors, setShowExceptions]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    if (getRegionsErrors) {
      setApiErrors(current => [...current, ...getRegionsErrors]);
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [getRegionsErrors, setApiErrors, setShowExceptions]);

  const handleAddVehicleClick = (request: IAddVehicleRequestModel) => {
    setApiErrors([]);
    request.regions = selectedRegions!;
    addVehicleSendRequest("settings/vehicles", "POST", request);
  };

  useEffect(() => {
    if (addVehicleResponseData && addVehicleResponseData.warningMessage) {
      setWarningMessage(addVehicleResponseData.warningMessage);
      setShowWarningMessage(true);
    }
  }, [addVehicleResponseData, setWarningMessage, setShowWarningMessage]);

  useEffect(() => {
    if (addVehicleErrors && addVehicleStatusCode !== 200) {
      setApiErrors(current => [...current, ...addVehicleErrors]);
      setShowExceptions(true);
    }
  }, [addVehicleStatusCode, addVehicleErrors, setApiErrors, setShowExceptions]);

  useEffect(() => {
    if (
      addVehicleStatusCode === 200
      && addVehicleResponseData
      && !addVehicleResponseData.warningMessage
    ) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [addVehicleStatusCode, addVehicleResponseData]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  const renderTrackingIds = () => {
    const trackingIdsDropdownList = [
      <option key="" value="">
        Unallocated
      </option>
    ];
    if (getUnallocatedTrackingDevicesData) {
      (
        getUnallocatedTrackingDevicesData as IGetTrackingDevicesResponseModel
      ).trackingDevices.forEach(trackingDevice => {
        trackingIdsDropdownList.push(
          <option key={trackingDevice.id} value={trackingDevice.id}>
            {trackingDevice.displayName}
          </option>
        );
      });
    }

    return trackingIdsDropdownList;
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="add-vehcile">
              Add Vehicle
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleAddVehicleClick)}>
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-collectors"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsCollectors }}
                    onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  >
                    Collectors
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-vehicle">
                    Add Vehicle
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || addVehicleIsSending}
                  aria-disabled={addButtonDisabled || addVehicleIsSending}
                  aria-label="Create vehicle"
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to collectors"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="pt-2" data-testid="formElements">
              <Container fluid>
                <Row className="d-flex mt-1 ms-1">
                  <Col xs={12}>
                    <div className="control-pane">
                      <div className="control-section" />
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="name">
                          <Form.Label
                            id="nameLabel"
                            className="horiz-form-label"
                          >
                            Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "name",
                                errors,
                                null
                              )}
                              aria-labelledby="nameLabel"
                              maxLength={50}
                              {...register("name")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="name"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="registration">
                          <Form.Label
                            id="registrationLabel"
                            className="horiz-form-label"
                          >
                            Registration (Mobile user login):
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "registration",
                                errors,
                                null
                              )}
                              aria-labelledby="registrationLabel"
                              maxLength={20}
                              {...register("registration")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="registration"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>

                        <Form.Group as={Col} controlId="password">
                          <Form.Label
                            id="passwordLabel"
                            className="horiz-form-label"
                          >
                            Password:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="password"
                              role="textbox"
                              minLength={6}
                              maxLength={20}
                              aria-invalid={ariaInvalidCheck(
                                "password",
                                errors,
                                null
                              )}
                              aria-required
                              aria-labelledby="passwordLabel"
                              {...register("aOmPassword")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="password"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="type">
                          <Form.Label
                            id="typeLabel"
                            className="horiz-form-label"
                          >
                            Type:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "type",
                                errors,
                                null
                              )}
                              aria-labelledby="typeLabel"
                              maxLength={50}
                              {...register("type")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="type"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2 mt-3">
                        <Form.Group
                          as={Col}
                          controlId="active"
                          className="d-flex align-items-center"
                        >
                          <Form.Label
                            id="activeLabel"
                            className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                          >
                            Active:
                          </Form.Label>
                          <Form.Check
                            {...register("active")}
                            type="switch"
                            className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                            aria-required="true"
                            aria-labelledby="activeLabel"
                            defaultChecked
                          />
                          <ErrorMessages
                            fieldName="active"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="regions">
                          <Form.Label
                            id="regionsLabel"
                            className="horiz-form-label"
                          >
                            Region(s):
                          </Form.Label>
                          <Col>
                            {regions && (
                              <DropDownTreeComponent
                                {...register("regions")}
                                ref={(d: DropDownTreeComponent) => {
                                  setDropDownTreeComponent(d);
                                }}
                                change={dropDownTreeComponentOnChange}
                                treeSettings={regionsTreeSettings}
                                fields={regionFields}
                                allowMultiSelection
                                showCheckBox
                                changeOnBlur={false}
                                mode="Box"
                                id="regionsDropDown"
                                htmlAttributes={{
                                  "data-testid": "ddlRegions",
                                  "aria-required": "true"
                                }}
                              />
                            )}
                            <NoData
                              visible={
                                !apiErrors && !getRegionsIsSending && !regions
                              }
                              description="No Region Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="regions"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="trackingId">
                          <Form.Label
                            id="trackingIdLabel"
                            className="horiz-form-label"
                          >
                            Tracking ID:
                          </Form.Label>
                          <Col>
                            <Form.Select
                              {...register("trackingId", {
                                onChange: () => canFormBeSubmitted()
                              })}
                              data-testid="ddlTrackingId"
                              aria-required={false}
                              aria-invalid={false}
                              aria-labelledby="trackingIdLabel"
                            >
                              {renderTrackingIds()}
                            </Form.Select>
                            <NoData
                              visible={
                                !apiErrors
                                && !getUnallocatedTrackingDevicesIsSending
                                && !getUnallocatedTrackingDevicesData
                              }
                              description="No Unallocated Tracking device Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="trackingId"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </section>
          </Card.Body>
        </Form>
      </Card>
      <WarningConfirmation
        title="Mobile User Warning"
        onHide={() => {
          navigate(RoutePathsEnum.SettingsCollectors);
        }}
        show={showWarningMessage}
      >
        <p>{warningMessage}</p>
      </WarningConfirmation>
    </Page>
  );
};

export default AddVehicle;
